import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Button from './Button';
import ToggleSwitch from './ToggleSwitch';

const EMPTY_FORM = { username: '', password: '', permissions: [] };

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function TeamPanel({ onError, currentUser }) {
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [permsRes, usersRes] = await Promise.all([
        axios.get('/api/permissions'),
        axios.get('/api/panel-users'),
      ]);
      setPermissions(permsRes.data?.permissions || []);
      setUsers(usersRes.data?.items || []);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const togglePermission = async (user, key) => {
    const next = user.permissions.includes(key)
      ? user.permissions.filter((item) => item !== key)
      : [...user.permissions, key];

    setBusyId(user.id);
    try {
      await axios.patch(`/api/panel-users/${user.id}`, { permissions: next });
      await load();
    } catch (err) {
      onError(err);
    } finally {
      setBusyId(null);
    }
  };

  const toggleEnabled = async (user) => {
    setBusyId(user.id);
    try {
      await axios.patch(`/api/panel-users/${user.id}`, { enabled: !user.enabled });
      await load();
    } catch (err) {
      onError(err);
    } finally {
      setBusyId(null);
    }
  };

  const resetPassword = async (user) => {
    const password = window.prompt(`Новий пароль для ${user.username} (мінімум 10 символів)`);
    if (!password) return;

    setBusyId(user.id);
    try {
      await axios.patch(`/api/panel-users/${user.id}`, { password });
      window.alert('Пароль змінено, активні сесії цього користувача завершено');
      await load();
    } catch (err) {
      onError(err);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (user) => {
    if (!window.confirm(`Видалити доступ для "${user.username}"?`)) return;

    setBusyId(user.id);
    try {
      await axios.delete(`/api/panel-users/${user.id}`);
      await load();
    } catch (err) {
      onError(err);
    } finally {
      setBusyId(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (creating) return;

    setCreating(true);
    try {
      await axios.post('/api/panel-users', form);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      onError(err);
    } finally {
      setCreating(false);
    }
  };

  const toggleFormPermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((item) => item !== key)
        : [...prev.permissions, key],
    }));
  };

  return (
    <div className="row g-3 align-items-start mt-1">
      <div className="col-12 col-xl-4">
        <div className="app-editor-card compact-card">
          <div className="app-editor-card-body compact-card-body">
            <div className="stats-section-title mb-3">Новий доступ</div>

            <form onSubmit={submit} className="team-form">
              <label className="f-field">
                <span className="f-label">Логін</span>
                <input
                  className="f-input"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="ivan.petrov"
                />
              </label>

              <label className="f-field">
                <span className="f-label">Пароль</span>
                <input
                  className="f-input"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="від 10 символів"
                />
              </label>

              <div className="f-field">
                <span className="f-label">Права</span>
                <div className="perm-list">
                  {permissions.map((item) => (
                    <ToggleSwitch
                      key={item.key}
                      label={item.label}
                      value={form.permissions.includes(item.key)}
                      onChange={() => toggleFormPermission(item.key)}
                    />
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                variant="dark"
                className="dashboard-btn"
                disabled={creating || !form.username.trim() || form.password.length < 10}
              >
                {creating ? 'Створюємо…' : 'Створити'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="col-12 col-xl-8">
        <div className="app-editor-card compact-card">
          <div className="app-editor-card-body compact-card-body">
            <div className="clicks-head">
              <div>
                <div className="stats-section-title">Учасники</div>
                <div className="stats-section-subtitle">
                  {loading ? 'Завантаження…' : `${users.length} акаунтів`}
                </div>
              </div>
            </div>

            {users.map((user) => (
              <div key={user.id} className={`team-card${user.enabled ? '' : ' is-off'}`}>
                <div className="team-card-head">
                  <div>
                    <div className="team-name">
                      {user.username}
                      {currentUser?.username === user.username ? <em className="team-you">це ви</em> : null}
                    </div>
                    <div className="team-meta">
                      Вхід: {formatDate(user.lastLoginAt)}
                      {user.lastLoginIp ? ` · ${user.lastLoginIp}` : ''}
                    </div>
                  </div>

                  <div className="team-actions">
                    <button
                      type="button"
                      className="fbtn"
                      disabled={busyId === user.id}
                      onClick={() => toggleEnabled(user)}
                    >
                      {user.enabled ? 'Вимкнути' : 'Увімкнути'}
                    </button>
                    <button
                      type="button"
                      className="fbtn"
                      disabled={busyId === user.id}
                      onClick={() => resetPassword(user)}
                    >
                      Пароль
                    </button>
                    <button
                      type="button"
                      className="fbtn is-danger"
                      disabled={busyId === user.id}
                      onClick={() => remove(user)}
                    >
                      Видалити
                    </button>
                  </div>
                </div>

                <div className={`perm-list is-grid${busyId === user.id ? ' is-busy' : ''}`}>
                  {permissions.map((item) => (
                    <ToggleSwitch
                      key={item.key}
                      label={item.label}
                      value={user.permissions.includes(item.key)}
                      onChange={() => togglePermission(user, item.key)}
                    />
                  ))}
                </div>
              </div>
            ))}

            {!loading && !users.length ? (
              <div className="settings-empty">Акаунтів ще немає</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
